/**
 * 에이전트가 쓰는 두 가지 바깥일 — 모델 호출과 화면 검색 — 을 미들서버에 넘긴다.
 *
 * **브라우저는 OpenAI 키도 Weaviate 키도 갖지 않는다.** 런타임이 `chat` 과 `shortlist`
 * 를 함수로 받도록 만들어진 이유가 이것이고, 그 함수의 구현이 여기 있다.
 */

import { retryTransient } from './runtime/retry';
import type { ExperienceCandidate } from './runtime/experience';

const CHAT = '/agent/chat';
const CHAT_STREAM = '/agent/chat/stream';
const SHORTLIST = '/agent/shortlist';
const EXPERIENCE_PROMOTE = '/agent/experiences/promote';
const ALIASES = '/agent/aliases';

export type Caller = { custcd: string; perid: string; token: string };

/**
 * 어느 단계의 호출인지. **모델 이름은 보내지 않는다** — 어느 모델·어느 추론 강도로
 * 푸는지는 미들서버 설정(`[AGENT] MODEL_<단계>` / `EFFORT_<단계>`)이 정한다.
 *
 * 브라우저가 모델을 고를 수 있으면 아무나 제일 비싼 모델을 꽂아 하루 한도를 태울 수
 * 있다. 그리고 단계마다 잘하는 모델이 다르다 — 싼 모델은 파이썬 생성은 잘하는데
 * 계획에서 약해서, 없는 칸 이름이나 목록에 없는 코드값을 짓는 것이 전부 그 자리다.
 *
 * 이름을 모르는 단계를 보내도 서버는 기본값으로 떨어질 뿐 실패하지 않는다.
 */
/**
 * 사람이 이 대화에 올린 파일. 본문은 base64 다.
 *
 * 런타임의 `UploadedFile` 과 같은 모양이다 — 게이트웨이가 받는 것이 이 모양이라
 * 양쪽이 갈리면 조용히 안 실린다.
 */
export type ChatFile = { name: string; mime: string; data: string };

export type ChatOptions = { step?: string; files?: ChatFile[] };

/**
 * 본문에 실을 것. **파일이 없으면 `files` 칸 자체를 넣지 않는다** — 게이트웨이는
 * 빈 배열이 아니라 없음을 보고 옛 모양(문자열 한 덩이)을 쓴다.
 */
function chatBody(prompt: string, options?: ChatOptions) {
  const files = options?.files ?? [];
  return { prompt, step: options?.step, ...(files.length ? { files } : {}) };
}

/**
 * 인증 미들웨어는 본문의 `token` 과 `window` 를 인증 서버에 넘겨 권한을 확인한다.
 * 에이전트는 화면이 아니지만 확인은 화면 단위로만 할 수 있다.
 *
 * 사용자 기본 권한인 `w_xusers` 를 쓴다. 이것이 없는 사용자는 ERP 자체를 쓸 수 없으므로
 * 에이전트도 못 쓰는 것이 맞다.
 */
const PERMISSION_WINDOW = 'w_xusers';

class AgentHttpError extends Error {
  constructor(readonly status: number, path: string) {
    super(`${path}: HTTP ${status}`);
    this.name = 'AgentHttpError';
  }
}

function isTransient(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return false;
  if (error instanceof AgentHttpError) return [429, 502, 503, 504].includes(error.status);
  // fetch 자체의 연결 실패는 브라우저에서 TypeError다.
  return error instanceof TypeError;
}

/**
 * 미들서버는 앱과 다른 출처에 있다 (`https://dev.elmansoft.com:12223`). 상대 경로로
 * 부르면 개발 서버 자신에게 가서 404 가 난다. 리포지토리들이 쓰는 환경변수를 그대로 쓴다.
 */
function serviceHost(): string {
  return String(process.env.REACT_APP_API_SERVICE_HOST ?? '').replace(/\/$/, '');
}

async function post(path: string, caller: Caller, body: object, signal?: AbortSignal) {
  return retryTransient(async () => {
    const response = await fetch(`${serviceHost()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ window: PERMISSION_WINDOW, ...caller, ...body }),
      signal,
    });
    if (!response.ok) throw new AgentHttpError(response.status, path);
    return response;
  }, isTransient, { signal });
}

/**
 * **이번 턴에 LLM 을 몇 번, 얼마나 불렀나.**
 *
 * 게이트웨이는 토큰 사용량을 안 돌려준다(`ErpService/agent.go` 에 `Usage` 가 없다).
 * 그래서 **보내는 쪽에서** 센다. 글자 수는 토큰이 아니지만, 어느 단계가 큰지를 가르는
 * 데는 충분하다 — 줄일 자리를 찾는 것이 목적이다.
 *
 * 이 숫자 없이 모델을 바꾸거나 판단기를 덧붙이면 **무엇이 줄었는지 못 잰다.**
 */
type Spent = { calls: number; sent: number; got: number; ms: number };

const spent = new Map<string, Spent>();

/** 턴이 시작될 때 부른다. 안 부르면 여러 턴이 한 덩이로 쌓인다. */
export function resetSpend(): void {
  spent.clear();
}

function record(step: string | undefined, sent: number, got: number, ms: number): void {
  const key = step ?? '기본';
  const one = spent.get(key) ?? { calls: 0, sent: 0, got: 0, ms: 0 };
  spent.set(key, {
    calls: one.calls + 1, sent: one.sent + sent, got: one.got + got, ms: one.ms + ms,
  });
  let calls = 0;
  let allSent = 0;
  let allGot = 0;
  let allMs = 0;
  const parts: string[] = [];
  spent.forEach((value, name) => {
    calls += value.calls;
    allSent += value.sent;
    allGot += value.got;
    allMs += value.ms;
    parts.push(`${name}×${value.calls} ${value.sent}자`);
  });
  // eslint-disable-next-line no-console
  console.debug('[agent] 씀',
    `${key} 보냄 ${sent}자 받음 ${got}자 ${ms}ms`,
    `| 누적 ${calls}번 보냄 ${allSent}자 받음 ${allGot}자 ${allMs}ms`,
    `| ${parts.join(' · ')}`);
}

export function makeChat(caller: Caller, signal?: AbortSignal) {
  return async (prompt: string, options?: ChatOptions): Promise<string> => {
    const began = Date.now();
    try {
      const response = await post(CHAT, caller, chatBody(prompt, options), signal);
      const body = await response.json();
      const text = String(body.text ?? '');
      record(options?.step, prompt.length, text.length, Date.now() - began);
      return text;
    } catch (error) {
      // 어떤 질문에서만 502 가 반복된다. 게이트웨이는 상류 실패 이유를 남기지 않으므로
      // 이쪽에서 보낸 것의 크기라도 적어 둔다 — 길이가 원인이면 그것으로 갈린다.
      // eslint-disable-next-line no-console
      console.debug('[agent] chat 실패', {
        단계: options?.step ?? '기본',
        글자: prompt.length,
        파일: (options?.files ?? []).length,
        앞: prompt.slice(0, 80).replace(/\s+/g, ' '),
      });
      throw error;
    }
  };
}

/**
 * SSE 를 읽는다. `EventSource` 는 POST 를 못 하므로 `fetch` 로 직접 읽는다.
 *
 * 서버는 `data: {"delta":"..."}` 를 흘리고 `{"done":true}` 로 끝낸다. 델타는 조각이지만
 * 보는 쪽에는 **이어 붙인 전체**를 준다 — 그 일을 화면마다 다시 하게 하지 않는다.
 */
export function makeChatStream(caller: Caller, signal?: AbortSignal) {
  return async (
    prompt: string,
    onDelta: (text: string) => void,
    options?: ChatOptions,
  ): Promise<string> => {
    const response = await post(CHAT_STREAM, caller, chatBody(prompt, options), signal);
    const reader = response.body?.getReader();
    // 흘려 받기가 안 되면 한 번에 받는다. 그때도 단계 이름을 잃지 않는다 —
    // 잃으면 이 경로만 조용히 다른 모델로 떨어진다.
    if (!reader) return makeChat(caller, signal)(prompt, options);

    const began = Date.now();
    const decoder = new TextDecoder();
    let buffer = '';
    let text = '';

    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // 한 번의 read 가 줄 하나로 딱 떨어지지 않는다. 마지막 조각은 다음 read 를 기다린다.
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data:')) continue;
        const payload = line.slice(5).trim();
        if (!payload) continue;
        let parsed: { delta?: string; done?: boolean; error?: string };
        try {
          parsed = JSON.parse(payload);
        } catch {
          continue;
        }
        if (parsed.error) throw new Error(parsed.error);
        if (parsed.done) {
          record(options?.step, prompt.length, text.length, Date.now() - began);
          return text;
        }
        if (parsed.delta) {
          text += parsed.delta;
          onDelta(text);
        }
      }
    }
    return text;
  };
}

export function makeShortlist(
  caller: Caller,
  signal?: AbortSignal,
  onExperiences?: (experiences: ExperienceCandidate[]) => void,
) {
  return async (question: string): Promise<string[]> => {
    try {
      const response = await post(SHORTLIST, caller, { question }, signal);
      const body = await response.json();
      onExperiences?.(Array.isArray(body.experiences) ? body.experiences : []);
      return Array.isArray(body.routes) ? body.routes.map(String) : [];
    } catch (error) {
      // 검색이 없어도 런타임은 화면 이름 겹침으로 떨어진다. 여기서 질문을 죽일 이유가 없다.
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return [];
    }
  };
}

function safeIntent(candidate: ExperienceCandidate): string {
  const requirements = candidate.requirements;
  const semantic = [
    ...(requirements?.dimensions ?? []),
    ...(requirements?.measures ?? []),
    ...(requirements?.operations ?? []),
  ].map(String).filter(Boolean);
  if (semantic.length) return semantic.join(' ');
  return candidate.recipe.flatMap((item) => [item.route, ...item.requiredColumns]).join(' ');
}

/** 질문 원문, 원본 행, 사용자 정보와 DOM 스냅샷을 제외한 레시피만 만든다. */
function safeExperience(candidate: ExperienceCandidate): object {
  return {
    version: 1,
    question: safeIntent(candidate),
    recipe: candidate.recipe.map((item) => ({
      route: item.route,
      rowsStateKey: item.rowsStateKey,
      conditionShape: item.conditionShape,
      requiredColumns: item.requiredColumns,
    })),
    validation: { valid: candidate.validation.valid, issues: [] },
  };
}

/**
 * 회사가 공유하는 이름 짝을 읽고, 이번에 배운 것을 올린다.
 *
 * 한 사람이 `덕봉빌딩 → 덕봉빌딩(두암정철)` 을 골라 준 것을 그 브라우저에만 두면
 * 옆자리는 같은 질문을 처음부터 다시 받는다. 서버가 없어도 대화는 계속돼야 하므로
 * 실패하면 빈 것을 돌려준다 — 그때는 각자 브라우저의 기억으로 돈다.
 */
export function makeAliases(caller: Caller) {
  return async (
    learned?: Record<string, string>,
    forget?: string[],
  ): Promise<Record<string, string>> => {
    try {
      // 잘못 배운 짝은 지울 수 있어야 한다. 한 사람이 되묻기에서 잘못 고른 것이
      // 회사 전체에 적용되고, 그 뒤로 그 말을 할 때마다 엉뚱한 값이 문서에 들어간다.
      const response = await post(ALIASES, caller, { learned: learned ?? {}, forget: forget ?? [] });
      const body = await response.json();
      return body?.aliases && typeof body.aliases === 'object' ? body.aliases : {};
    } catch {
      return {};
    }
  };
}

export function makeExperiencePromote(caller: Caller) {
  return async (candidate: ExperienceCandidate): Promise<void> => {
    await post(EXPERIENCE_PROMOTE, caller, {
      feedback: 'positive', candidate: safeExperience(candidate),
    });
  };
}
